/* ════════════════════════════════════════════════════════════════════════════════════════════════════════════════
   THE CARDS IN THE CART, KEPT ON THIS DEVICE, SO "EDIT CARD" CAN REOPEN ONE (Part 4, Daniel, 9 Oct: "Edit card
   reopens the card in the builder with its people kept").

   A cart line carries the card BAKED: four photos at print size, the note, the look. The customer's own photos, their
   crops and filters, the inside top's choices are not on the order and cannot be rebuilt from it. They are in the
   builder's draft (pp_draft_v3 in localStorage, the pixels in IndexedDB pp_photos) until Add to cart clears it. So:

     at Add to cart   (the builder; wraps addToCart the way builder-b.js wraps today's component) the draft as saved
                      and its photos are copied into this file's own IndexedDB database, pp_cart_cards, keyed by the
                      card's line key: _cartKey(), the part of every `_pp_line` before the first colon. The last 10
                      cards are kept, none older than 30 days. Nothing about the draft or the order changes.
     Edit card        (the cart, assets/cart-b.js) writes the kept card back as the draft, with the people and the
                      sender AS THE CART HAS THEM NOW, and opens the builder on Preview with the same line key, so its
                      Add to cart replaces those lines (configurator.js addToCart: replace, never duplicate).
     To me            the kept draft says whether the card was To me, someone else or several: the order does not,
                      and the cart uses this to say "To you" on this device (plan §2).

   A card made on another device, or before this shipped, is simply not here: the cart then offers no Edit card.
   Plan: projects/pop-cards/docs/plans/2026-10-09-cart-redesign-plan.md §4 (venture repo).
   ════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  window.PP = window.PP || {};

  var DB = 'pp_cart_cards', STORE = 'cards', KEEP = 10, MAX_AGE = 30 * 864e5;
  var DRAFT_KEY = 'pp_draft_v3', DRAFT_FLAG = 'pp_draft_flag_v1';
  var EDIT_KEY = 'pp_cart_edit_v1';            // sessionStorage: the line key the builder adopts, set by Edit card
  var ADDED_KEY = 'pp_cart_added_v1';          // sessionStorage: the card just added, for the cart's green line
  var PHOTO_KEYS = ['pp_photo_0', 'pp_photo_1', 'pp_photo_2', 'pp_photo_3', 'pp_top_photo'];

  function open() {
    return new Promise(function (resolve, reject) {
      if (!window.indexedDB) { reject(new Error('no_idb')); return; }
      var req = indexedDB.open(DB, 1);
      req.onupgradeneeded = function () { req.result.createObjectStore(STORE); };
      req.onsuccess = function () { resolve(req.result); };
      req.onerror = function () { reject(req.error); };
    });
  }
  function tx(mode, run) {
    return open().then(function (db) {
      return new Promise(function (resolve, reject) {
        var t = db.transaction(STORE, mode), out;
        run(t.objectStore(STORE), function (v) { out = v; });
        t.oncomplete = function () { resolve(out); };
        t.onerror = t.onabort = function () { reject(t.error); };
      });
    });
  }
  function all() {
    return tx('readonly', function (st, set) {
      var r = st.getAll(), k = st.getAllKeys(), vals;
      r.onsuccess = function () { vals = r.result; };
      k.onsuccess = function () { set((k.result || []).map(function (key, i) { return { key: key, card: vals && vals[i] }; })); };
    });
  }
  function get(base) {
    if (!base) return Promise.resolve(null);
    return tx('readonly', function (st, set) { var r = st.get(base); r.onsuccess = function () { set(r.result || null); }; })
      .catch(function () { return null; });
  }
  function del(base) { return tx('readwrite', function (st) { st.delete(base); }).catch(function () {}); }
  function prune() {
    return all().then(function (list) {
      var now = Date.now();
      list.sort(function (a, b) { return ((b.card && b.card.at) || 0) - ((a.card && a.card.at) || 0); });
      var drop = list.filter(function (x, i) { return i >= KEEP || !x.card || now - (x.card.at || 0) > MAX_AGE; });
      return Promise.all(drop.map(function (x) { return del(x.key); }));
    }).catch(function () {});
  }

  /** Copy the builder's draft, as saved, and its photos, under the card's line key. */
  function keep(base) {
    var draft;
    try { draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch (e) { draft = null; }
    if (!base || !draft || draft.v !== 3) return Promise.resolve(false);
    var idb = window.PP.idb;
    var reads = PHOTO_KEYS.map(function (k) { return idb ? idb.get(k).catch(function () { return null; }) : Promise.resolve(null); });
    return Promise.all(reads).then(function (blobs) {
      var photos = {};
      PHOTO_KEYS.forEach(function (k, i) { if (blobs[i]) photos[k] = blobs[i]; });
      var card = { at: Date.now(), draft: draft, photos: photos, sendTo: draft.sendTo || '' };
      return tx('readwrite', function (st) { st.put(card, base); });
    }).then(prune).then(function () { return true; }, function () { return false; });
  }

  /** Is there a card on the go in the builder right now (other than one we are about to write)? */
  function draftOnTheGo() {
    try {
      if (localStorage.getItem(DRAFT_FLAG) !== '1') return null;
      return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
    } catch (e) { return null; }
  }

  /** Write a kept card back as the builder's draft, with the cart's people and sender, and mark the line key. */
  function restore(base, people, sender, sendTo) {
    return get(base).then(function (card) {
      if (!card) throw new Error('not_kept');
      var d = JSON.parse(JSON.stringify(card.draft));
      d.ts = Date.now();
      if (people && people.length) d.recipients = people;
      d.sender = sender || { firstName: '', lastName: '', line1: '', line2: '', city: '', state: '', zip: '', abroad: false };
      d.sendTo = sendTo || (people && people.length > 1 ? 'several' : (card.sendTo || d.sendTo || 'other'));
      var idb = window.PP.idb;
      // ⛔ Never a card without its pixels: a draft whose photos are not written back opens blank and cannot be added.
      if (!idb) throw new Error('no_photo_store');
      return idb.clear().then(function () {
        return Promise.all(Object.keys(card.photos || {}).map(function (k) { return idb.put(k, card.photos[k]); }));
      }).then(function () {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
        localStorage.setItem(DRAFT_FLAG, '1');
        sessionStorage.setItem(EDIT_KEY, JSON.stringify({ base: base, at: Date.now(), fp: fingerprint(d) }));
        // The card opens again, as it does on "Continue card" (pp-draft-cta).
        try { sessionStorage.removeItem('pp_opened_v2'); } catch (e) {}
      });
    });
  }

  function justAdded() {
    try {
      var a = JSON.parse(sessionStorage.getItem(ADDED_KEY) || 'null');
      sessionStorage.removeItem(ADDED_KEY);
      return a && Date.now() - a.at < 120000 ? a : null;
    } catch (e) { return null; }
  }

  window.PP.cartCards = { keep: keep, get: get, remove: del, restore: restore, draftOnTheGo: draftOnTheGo,
                          justAdded: justAdded, EDIT_KEY: EDIT_KEY };

  /* ── In the builder: keep the card at Add to cart, and adopt the line key Edit card left ──────────────────────
     Wraps PP.configurator (today's component, which PP.builderB builds on) and PP.builderB (for B's landing) before
     Alpine calls them: this file loads after both, and Alpine, the layout's, after it.
     ⛔ THE LINE KEY IS ADOPTED ONLY FOR THE RESTORED CARD. The mark carries a fingerprint of the card written back
     (its design and its photos); a different card started in the same tab never inherits the key, or its Add to cart
     would replace the edited card's lines. A restored card whose photo was replaced and the page reloaded loses the
     key, and its Add to cart adds beside the old lines instead of over them: a duplicate the cart shows, never a loss. */
  function fingerprint(d) {
    if (!d || !d.card) return '';
    var des = d.card.design && (d.card.design.id || d.card.design.artKey) || '';
    return des + '|' + (d.photos || []).map(function (p) { return (p && p.name || '') + ':' + (p && p.size || ''); }).join(',');
  }
  function editMark() {
    var m;
    try { m = JSON.parse(sessionStorage.getItem(EDIT_KEY) || 'null'); } catch (e) { m = null; }
    if (!m || !m.base || Date.now() - m.at > 6 * 36e5) return null;
    var d; try { d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch (e) { d = null; }
    return d && fingerprint(d) === m.fp ? m : null;
  }
  function wrap(name, hook) {
    var make = window.PP[name];
    if (typeof make !== 'function' || make.__ppCartCards) return;
    var wrapped = function () { return hook(make.apply(this, arguments)); };
    wrapped.__ppCartCards = true;
    window.PP[name] = wrapped;
  }
  wrap('configurator', function (c) {
    var edit = editMark();
    if (edit) c._ppLine = edit.base;
    var add = c.addToCart;
    c.addToCart = function () {
      var self = this, args = arguments, base;
      try { base = this._cartKey(); } catch (e) { base = null; }
      try { if (typeof this.saveNow === 'function') this.saveNow(); } catch (e) { /* the last save stands */ }
      // Never in the way of the add: a failed keep costs only Edit card for this one card.
      var kept = base && !this.artist && !this.org ? keep(base).catch(function () {}) : Promise.resolve();
      return kept.then(function () {
        try {
          sessionStorage.setItem(ADDED_KEY, JSON.stringify({ base: base, at: Date.now() }));
          sessionStorage.removeItem(EDIT_KEY);
        } catch (e) { /* the green line is a nicety */ }
        return add.apply(self, args);
      });
    };
    return c;
  });
  wrap('builderB', function (c) {
    var land = c.ppbLand;
    if (typeof land === 'function' && editMark()) {
      c.ppbLand = function () {
        var was = this._ppbLanded;
        land.apply(this, arguments);
        if (!was && this._ppbLanded && typeof this.ppbGo === 'function') this.ppbGo(typeof this.ppbShowStep === 'function' ? this.ppbShowStep() : 'preview');   // Look since the five steps (19 Oct)
      };
    }
    return c;
  });
})();
